import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { FooterComponent } from '../../../../shared/components/footer/footer.component';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../../../shared/components/asset-header/asset-header.component';
import { DiscussionSidebarComponent } from '../../discussion-sidebar/discussion-sidebar.component';
import { DiscussionListComponent, DiscussionItem } from '../../shared/discussion-list/discussion-list.component';
import { DiscussionApiService } from '@angular-workspace/services';
import { KeycloakService } from 'keycloak-angular';
import { LoginModalComponent } from '../../../../shared/components/login-modal/login-modal.component';
import { ConfirmationDialogComponent } from '../../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { Router } from '@angular/router';
import { ToasterService } from '../../../../services/toaster.service';

@Component({
  selector: 'app-discussion-post-submission',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    DiscussionSidebarComponent,
    DiscussionListComponent,
    LoginModalComponent,
    ConfirmationDialogComponent
  ],
  templateUrl: './post-submission.component.html',
  styleUrl: './post-submission.component.scss',
})
export class DiscussionPostSubmissionComponent implements OnInit {
  private router = inject(Router);
  private discussionApiService = inject(DiscussionApiService);
  private keycloakService = inject(KeycloakService);
  private toasterService = inject(ToasterService);

  discussions: DiscussionItem[] = [];
  isLoggedIn: boolean = false;
  showLoginModal: boolean = false;
  showEditConfirmModal: boolean = false;
  showDeleteConfirmModal: boolean = false;
  discussionToEdit: DiscussionItem | null = null;
  discussionToDelete: DiscussionItem | null = null;
  deleteInProgress: boolean = false;
  activeTab: string = 'post-submission';

  /** Tab for filtering by submission status */
  statusTab: 'all' | 'pending' | 'approved' | 'changes-required' | 'rejected' = 'all';

  /** Pagination */
  currentPage: number = 1;
  totalPages: number = 1;
  totalCount: number = 0;
  readonly itemsPerPage: number = 10;

  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Discussions', route: '/discussions' },
      { label: 'Post Submission' }
    ],
    title: 'Post Submission',
    description: 'View your submitted discussions'
  };

  ngOnInit(): void {
    this.checkLoginStatus();
    this.fetchSubmittedDiscussions();
  }

  async checkLoginStatus(): Promise<void> {
    try {
      this.isLoggedIn = await this.keycloakService.isLoggedIn();
    } catch (error) {
      console.error('Error checking login status:', error);
      this.isLoggedIn = false;
    }
  }

  /** Get status filter array for the current status tab */
  private getStatusFilters(): string[] {
    switch (this.statusTab) {
      case 'all':
        return ['PENDING', 'CHANGES_REQUIRED', 'REJECTED', 'APPROVED'];
      case 'pending':
        return ['PENDING'];
      case 'approved':
        return ['APPROVED'];
      case 'changes-required':
        return ['CHANGES_REQUIRED'];
      case 'rejected':
        return ['REJECTED'];
      default:
        return ['PENDING', 'CHANGES_REQUIRED', 'REJECTED', 'APPROVED'];
    }
  }

  setStatusTab(tab: 'all' | 'pending' | 'approved' | 'changes-required' | 'rejected'): void {
    this.statusTab = tab;
    this.currentPage = 1;
    this.fetchSubmittedDiscussions();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.fetchSubmittedDiscussions();
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    const total = this.totalPages;
    if (total <= 0) return pages;
    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  async fetchSubmittedDiscussions(): Promise<void> {
    try {
      const response = await this.discussionApiService.getMyDiscussion({
        filters: {
          type: null,
          status: this.getStatusFilters(),
          tags: null
        },
        sort_by: 'newest',
        page: this.currentPage,
        limit: this.itemsPerPage,
        pinned: true
      });

      const list = this.getDiscussionsListFromResponse(response);
      this.discussions = list.map((item: any) => this.transformToDiscussionItem(item));

      const meta = response?.data?.meta ?? response?.meta;
      if (meta) {
        this.totalCount = meta.total_count ?? meta.totalCount ?? 0;
        this.totalPages = meta.total_pages ?? meta.totalPages ?? 1;
      } else {
        this.totalPages = 1;
        this.totalCount = this.discussions.length;
      }
    } catch (error) {
      console.error('Error fetching submitted discussions:', error);
      this.discussions = [];
      this.totalPages = 1;
      this.totalCount = 0;
    }
  }

  /** Extract discussions array from API response (handles data.discussions, data, etc.) */
  private getDiscussionsListFromResponse(response: any): any[] {
    if (!response) return [];
    const data = response.data ?? response;
    if (Array.isArray(data)) return data;
    if (data?.discussions && Array.isArray(data.discussions)) return data.discussions;
    if (data?.discussion && Array.isArray(data.discussion)) return data.discussion;
    return [];
  }

  transformToDiscussionItem(item: any): DiscussionItem {
    return {
      id: item.id || item._id || '',
      title: item.title || 'Untitled Discussion',
      description: item.description || item.content || '',
      author: item.user?.name || item.author?.name || item.author || item.created_by || 'Unknown',
      category: item.category ?? '',
      discussionType:
        [item.type, item.discussion_type]
          .map((v) => (v != null && String(v).trim() ? String(v).trim() : ''))
          .find((s) => s) || undefined,
      replies: item.reply_count ?? item.replies ?? item.comment_count ?? 0,
      views: item.view_count ?? item.views ?? 0,
      lastActivity: item.updated_at ?? item.updatedAt ?? item.last_activity ?? item.created_at ?? item.createdAt ?? new Date().toISOString(),
      statusLabel: this.getStatusLabel(item.status),
      status: item.status,
      isPinned: item.is_pinned ?? item.pinned ?? false,
      isBookmarked: item.is_bookmarked ?? item.bookmarked ?? false,
      tags: item.tags || [],
      attachments: item.attachments || []
    };
  }

  private getStatusLabel(status: string | undefined): string | undefined {
    if (!status) return undefined;
    const labels: Record<string, string> = {
      APPROVED: 'Approved',
      PENDING: 'Pending',
      REJECTED: 'Rejected',
      CHANGES_REQUIRED: 'Changes Required'
    };
    return labels[status] || status;
  }

  createNewDiscussion(): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    this.router.navigate(['/discussions/create-new']);
  }

  async onBookmarkClick(event: { event: Event; discussion: DiscussionItem }): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    const newBookmarkState = !event.discussion.isBookmarked;
    const previousState = event.discussion.isBookmarked;
    event.discussion.isBookmarked = newBookmarkState;
    try {
      if (previousState) {
        await this.discussionApiService.unbookmarkDiscussion(event.discussion.id);
      } else {
        await this.discussionApiService.bookmarkDiscussion(event.discussion.id);
      }
      this.toasterService.success(
        newBookmarkState
          ? 'This discussion has been added to your bookmarks. You can open it anytime from My Bookmarks.'
          : 'This discussion has been removed from your bookmarks.'
      );
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      event.discussion.isBookmarked = previousState;
      this.toasterService.error((error as any)?.error?.detail || 'Failed to update bookmark');
    }
  }

  async onPinClick(event: { event: Event; discussion: DiscussionItem }): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    const newPinState = !event.discussion.isPinned;
    const previousState = event.discussion.isPinned;
    event.discussion.isPinned = newPinState;
    try {
      if (previousState) {
        await this.discussionApiService.unpinDiscussion(event.discussion.id);
      } else {
        await this.discussionApiService.pinDiscussion(event.discussion.id);
      }
      this.toasterService.success(
        newPinState
          ? 'This discussion is now pinned. Find it under My Pinned in Discussions.'
          : 'This discussion has been unpinned.'
      );
    } catch (error) {
      console.error('Error toggling pin:', error);
      event.discussion.isPinned = previousState;
      this.toasterService.error((error as any)?.error?.detail || 'Failed to update pin');
    }
  }

  onEditClick(event: { event: Event; discussion: DiscussionItem }): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    this.discussionToEdit = event.discussion;
    this.showEditConfirmModal = true;
  }

  onConfirmEdit(): void {
    if (!this.discussionToEdit) return;
    this.router.navigate(['/discussions/create-new'], {
      queryParams: { edit: this.discussionToEdit.id }
    });
    this.showEditConfirmModal = false;
    this.discussionToEdit = null;
  }

  onCancelEdit(): void {
    this.showEditConfirmModal = false;
    this.discussionToEdit = null;
  }

  onDeleteClick(event: { event: Event; discussion: DiscussionItem }): void {
    this.discussionToDelete = event.discussion;
    this.showDeleteConfirmModal = true;
  }

  onCancelDelete(): void {
    this.showDeleteConfirmModal = false;
    this.discussionToDelete = null;
  }

  async onConfirmDelete(): Promise<void> {
    if (!this.discussionToDelete) return;
    this.deleteInProgress = true;
    try {
      await this.discussionApiService.deleteMyDiscussion(this.discussionToDelete.id);
      this.discussions = this.discussions.filter(d => d.id !== this.discussionToDelete!.id);
      this.toasterService.success('Discussion deleted successfully');
      this.showDeleteConfirmModal = false;
      this.discussionToDelete = null;
    } catch (error) {
      this.toasterService.error((error as any)?.error?.detail || 'Failed to delete discussion');
    } finally {
      this.deleteInProgress = false;
    }
  }
}
